import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowUpRight, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { STATUS, addressLine, fmtEuro } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { OrderTable } from "@/components/OrderTable";
import { SlaChip, PriorityBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Kpi = ({ label, value, sub, tone = "", onClick, testId, delay = 0 }) => (
  <button onClick={onClick} data-testid={testId} style={{ animationDelay: `${delay}ms` }}
    className="group fade-up border bg-white p-5 text-left transition-colors duration-150 hover:border-primary">
    <div className="flex items-start justify-between"><div className="micro-label">{label}</div><ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" /></div>
    <div className={`mt-4 font-mono text-3xl font-semibold tracking-tight xl:text-4xl ${tone}`}>{value ?? "–"}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </button>
);

const StatusBar = ({ byStatus }) => {
  const total = Object.values(byStatus || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="border bg-white p-6" data-testid="status-distribution">
      <div className="micro-label mb-4">Avanzamento ordini per stato</div>
      <div className="flex h-3 w-full overflow-hidden">
        {Object.entries(byStatus || {}).map(([k, v]) => v > 0 && <div key={k} style={{ width: `${(v / total) * 100}%`, background: STATUS[k].color }} />)}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
        {Object.entries(byStatus || {}).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5" style={{ background: STATUS[k].color }} /><span className="text-muted-foreground">{STATUS[k].label}</span>
            <span className="ml-auto font-mono font-semibold">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectorChart = ({ bySvc }) => {
  const max = Math.max(...Object.values(bySvc || {}), 1);
  return (
    <div className="border bg-white p-6" data-testid="sector-chart">
      <div className="micro-label mb-4">Produzione per settore</div>
      {["DELIVERY", "ASSURANCE", "CONSTRUCTION"].map((s) => (
        <div key={s} className="mb-3">
          <div className="mb-1 flex justify-between text-sm"><span>{s.charAt(0) + s.slice(1).toLowerCase()}</span><span className="font-mono">{fmtEuro(bySvc?.[s] || 0)}</span></div>
          <div className="h-2.5 bg-muted"><div className={`h-full ${s === "ASSURANCE" ? "bg-urgent" : "bg-primary"}`} style={{ width: `${((bySvc?.[s] || 0) / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

const UrgentList = ({ orders }) => {
  const navigate = useNavigate();
  const urgent = orders.filter((o) => o.service_type === "ASSURANCE" && ["RECEIVED", "TO_PLAN", "PLANNED", "IN_PROGRESS", "SUSPENDED"].includes(o.status))
    .sort((a, b) => (a.sla_due_at || "").localeCompare(b.sla_due_at || "")).slice(0, 6);
  return (
    <div className="border bg-white" data-testid="urgent-list">
      <div className="flex items-center gap-2 border-b px-6 py-4"><Zap className="h-4 w-4 text-urgent" /><span className="micro-label">Urgenze Assurance per SLA</span></div>
      {urgent.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nessun ticket aperto</div>}
      {urgent.map((o) => (
        <button key={o.id} onClick={() => navigate(`/ordini/${o.id}`)} className="flex w-full items-center justify-between gap-3 border-b px-6 py-3 text-left transition-colors last:border-0 hover:bg-orange-50/50">
          <div className="min-w-0"><div className="font-mono text-sm font-semibold">{o.external_order_id}</div><div className="truncate text-xs text-muted-foreground">{o.assurance?.symptom} · {addressLine(o.address)}</div></div>
          <div className="flex shrink-0 flex-col items-end gap-1"><PriorityBadge priority={o.priority} /><SlaChip order={o} /></div>
        </button>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { stats } = useOutletContext();
  const { companyId, parties } = useData();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState("all");
  const [customer, setCustomer] = useState("all");
  const [orders, setOrders] = useState([]);
  const [fin, setFin] = useState(null);

  useEffect(() => {
    const params = { company_id: companyId || undefined, service_type: service === "all" ? undefined : service, customer_id: customer === "all" ? undefined : customer, limit: 200 };
    api.get("/orders", { params }).then((r) => setOrders(r.data)).catch(() => {});
  }, [companyId, service, customer]);

  useEffect(() => {
    if (!can("finance:read")) return;
    const from = new Date(); from.setDate(1);
    api.get("/production/summary", { params: { company_id: companyId || undefined, date_from: from.toISOString().slice(0, 10) } }).then((r) => setFin(r.data)).catch(() => {});
  }, [companyId, can]);

  const month = new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={`Schermata A · ${month}`} title="Centro operativo">
        <Select value={service} onValueChange={setService}>
          <SelectTrigger className="w-40 bg-white" data-testid="dashboard-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le attività</SelectItem><SelectItem value="DELIVERY">Delivery</SelectItem><SelectItem value="ASSURANCE">Assurance</SelectItem><SelectItem value="CONSTRUCTION">Construction</SelectItem></SelectContent>
        </Select>
        <Select value={customer} onValueChange={setCustomer}>
          <SelectTrigger className="w-48 bg-white" data-testid="dashboard-customer-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i committenti</SelectItem>{parties.filter((p) => p.roles.includes("customer")).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      {fin && (
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="executive-kpis">
          <Kpi label="Interventi completati" value={fin.completed} sub="Delivery + Assurance, mese" testId="kpi-completed" onClick={() => navigate("/produzione")} />
          <Kpi label="Produzione eseguita" value={fmtEuro(fin.totals.executed)} sub="Valore del mese" testId="kpi-executed" onClick={() => navigate("/produzione")} delay={30} />
          <Kpi label="Produzione validata" value={fmtEuro(fin.totals.validated)} testId="kpi-validated" tone="text-emerald-700" onClick={() => navigate("/produzione")} delay={60} />
          <Kpi label="Fatturabile" value={fmtEuro(fin.totals.billable)} testId="kpi-billable" onClick={() => navigate("/produzione")} delay={90} />
          <Kpi label="Interventi KO" value={`${fin.ko_pct}%`} sub="Da analizzare" tone="text-red-600" testId="kpi-ko" onClick={() => navigate("/ordini")} delay={120} />
          <Kpi label="Margine operativo" value={fin.totals.margin_pct != null ? `${fin.totals.margin_pct}%` : "—"} sub="Ricavi − costi diretti" testId="kpi-margin" onClick={() => navigate("/produzione")} delay={150} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Kpi label="Ordini aperti" value={stats?.open} onClick={() => navigate("/ordini")} testId="kpi-open" />
        <Kpi label="Ricevuti da verificare" value={stats?.received} onClick={() => navigate("/ordini?status=RECEIVED")} testId="kpi-received" delay={30} />
        <Kpi label="Da pianificare + sospesi" value={stats?.to_plan} onClick={() => navigate("/pianificazione")} testId="kpi-to-plan" delay={60} />
        <Kpi label="Urgenti" value={stats?.urgent} tone="text-urgent" onClick={() => navigate("/ordini?service_type=ASSURANCE")} testId="kpi-urgent" delay={90} />
        <Kpi label="SLA scaduti" value={stats?.sla_breach} tone="text-red-600" onClick={() => navigate("/ordini?service_type=ASSURANCE")} testId="kpi-sla-breach" delay={120} />
        <Kpi label="Da validare" value={stats?.to_validate} tone="text-primary" onClick={() => navigate("/produzione")} testId="kpi-to-validate" delay={150} />
      </div>
      <div className="mt-6 grid items-start gap-6 xl:grid-cols-3">
        <StatusBar byStatus={stats?.by_status} />
        {fin ? <SectorChart bySvc={fin.by_service} /> : <div />}
        <UrgentList orders={orders} />
      </div>
      <div className="mb-4 mt-10 flex items-end justify-between">
        <h2 className="font-display text-xl font-bold">Ordini recenti</h2>
        <span className="font-mono text-xs text-muted-foreground">{orders.length} risultati</span>
      </div>
      <OrderTable orders={orders.slice(0, 20)} testId="dashboard-orders-table" />
    </div>
  );
}
